import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Button } from "react-bootstrap";
import { tokens } from "../../theme";

const heading = { fontWeight: 700, color: tokens.text, margin: "18px 0 6px" };
const table = { width: "100%", borderCollapse: "collapse", fontSize: "13px" };
const cell = { borderBottom: `1px solid ${tokens.border}`, padding: "6px 8px", verticalAlign: "top", textAlign: "left" };

/** "How tournaments work" button and guide for admins. */
const TournamentGuide = ({ label = "How tournaments work" }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="outline-secondary" style={{ height: "fit-content" }} onClick={() => setOpen(true)}>
        <HelpOutlineIcon style={{ fontSize: "18px", marginRight: "4px", verticalAlign: "-4px" }} />
        {label}
      </Button>

      <Dialog maxWidth="md" fullWidth open={open} onClose={() => setOpen(false)}>
        <DialogTitle>How tournaments work</DialogTitle>
        <DialogContent dividers style={{ fontSize: "14px", lineHeight: 1.6 }}>
          <p style={{ marginTop: 0 }}>
            Players join a tournament, play the games you choose with Sweeps Coins, and the best players share a prize
            pool. Everything after you set it up happens automatically.
          </p>

          <div style={heading}>Setting one up, step by step</div>
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            <li>
              <b>Add Tournament</b>: give it a name, a start and end, a win mechanic, a prize pool and the number of
              winners. Check the <b>Summary</b> at the bottom of the form, then Save.
            </li>
            <li>
              <b>🎮 Games</b> in the list: pick the games that count. Players only see the tournament once it has at
              least one active game.
            </li>
            <li>
              <b>Wait</b>: players press Join on the site and play. Watch the standings with 🏆 <b>Players &amp; Payout</b>.
            </li>
            <li>
              <b>It ends</b>: final ranks and prizes are saved automatically a few seconds after the end time.
            </li>
            <li>
              <b>Payout</b>: winners are paid automatically shortly after (the payout delay). Nothing to press.
            </li>
          </ol>

          <div style={heading}>Status in the list</div>
          <table style={table}>
            <tbody>
              <tr><td style={cell}><b>Upcoming / Live</b></td><td style={cell}>Players can join. You can still edit it and change its games.</td></tr>
              <tr><td style={cell}><b>Ended</b></td><td style={cell}>The end time passed; results are being saved.</td></tr>
              <tr><td style={cell}><b>Finished - results saved</b></td><td style={cell}>Final ranks and prizes are saved; waiting for the automatic payout. It can no longer be edited.</td></tr>
              <tr><td style={cell}><b>Paid</b></td><td style={cell}>Every winner was paid. Kept for history; cannot be edited or deleted.</td></tr>
            </tbody>
          </table>

          <div style={heading}>What each field means</div>
          <table style={table}>
            <tbody>
              <tr><td style={cell}><b>Win Mechanic</b></td><td style={cell}><b>Total Win</b>: score = SC won. <b>Total Bet</b>: score = SC bet (rewards playing a lot).</td></tr>
              {/* Minimum bet per spin is no longer asked for.
              <tr><td style={cell}><b>Minimum Bet</b></td><td style={cell}>Spins with a smaller bet do not count. Leave empty to count every spin.</td></tr> */}
              <tr><td style={cell}><b>Minimum total bet to win</b></td><td style={cell}>Stops one tiny spin from winning. 100 → a player must bet 100 SC in total. 0 = no minimum.</td></tr>
              <tr><td style={cell}><b>Pool type</b></td><td style={cell}><b>Fixed</b>: players share exactly the Prize Pool. <b>Dynamic</b>: the pool starts at 0 and is a % of all bets made so far (2% and 50,000 SC bet → 1,000 SC), and never grows past the Maximum pool. The final pool uses the total bets at the end time.</td></tr>
              <tr><td style={cell}><b>Prize Pool / Maximum pool</b></td><td style={cell}>Fixed: the amount paid. Dynamic: the most it can ever pay.</td></tr>
              <tr><td style={cell}><b>Number of Winners</b></td><td style={cell}>How many top players get a prize.</td></tr>
              <tr><td style={cell}><b>Prize Steepness</b></td><td style={cell}>How much more 1st gets than the others. 1 (default): 2nd gets half of 1st, 3rd a third. 0: everyone equal.</td></tr>
              <tr><td style={cell}><b>Active</b></td><td style={cell}>Off hides it from players.</td></tr>
            </tbody>
          </table>

          <p style={{ margin: "12px 0 0" }}>
            With a fixed pool, a quiet tournament still pays the whole pool, even if only one player joins and spins
            once. A dynamic pool, or a minimum total bet, prevents that.
          </p>
          {/* Protection settings are switched off for now.
          <div style={heading}>Protection (optional)</div>
          <table style={table}>
            <tbody>
              <tr><td style={cell}><b>Prize cap</b></td><td style={cell}>Nobody wins more than this many times what they bet. 10 → bet 20 SC, win at most 200 SC.</td></tr>
              <tr><td style={cell}><b>Winners as % of players</b></td><td style={cell}>Fewer winners when few play. 20 → 10 players give 2 winners.</td></tr>
              <tr><td style={cell}><b>Pool by number of players</b></td><td style={cell}>Milestones, e.g. 0+ players: 500 SC, 50+: 2,500 SC, 200+: 10,000 SC.</td></tr>
            </tbody>
          </table> */}

          <div style={heading}>Good starting settings</div>
          <table style={table}>
            <tbody>
              <tr><td style={cell}><b>Simple</b></td><td style={cell}>Pool 1,000 SC, 10 winners, steepness 1, no protection.</td></tr>
              <tr><td style={cell}><b>Safe for quiet days</b></td><td style={cell}>Dynamic pool 2%, maximum 10,000 SC, 50 winners, minimum total bet 100 SC.</td></tr>
            </tbody>
          </table>

          <div style={heading}>If something goes wrong</div>
          <ul style={{ paddingLeft: "20px", margin: 0 }}>
            <li><b>Calculate Results</b> (in 🏆) recalculates ranks before payout.</li>
            <li><b>Force Settle</b> (in 🏆) saves results and pays now, if the automatic payout did not run.</li>
            <li><b>Delete</b> removes a tournament nobody was paid from.</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TournamentGuide;
